import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { error, log } from "../tools/helpers/ConsoleHelper.ts";
import { importAesKey } from "../tools/helpers/SecurityHelper.ts";
import {
  getBase64SharedKey,
  getWebSocketUrl,
} from "../tools/ConfigurationReader.ts";
import type { MarketMarketIO } from "../generated/fresh-guard-api";
import { lsClear, lsLoad } from "../tools/Storage.ts";
import StorageEnum from "../enums/Storage.enum.ts";
import type { WebSocketType } from "./WebSocketType.ts";
import { toast } from "react-toastify";
import { toastOptions } from "../tools/helpers/ToastHelper.ts";
import { useWebSocket as useWebSocketProvider } from "../provider/web-socket/WebSocket.hook.ts";
import { useTranslation } from "react-i18next";
import { useApplicationReady } from "../tools/hooks/useApplicationReady.ts";
import type { WebSocketSetupType } from "./WebSocketSetupType.ts";
import { useLogging } from "../provider/logging/Logging.hook.ts";
import { v4 } from "uuid";
import MessageEnum from "./MessageEnum.ts";
import { reloadPage } from "../tools/Routing.ts";
import { queryKeys } from "../apis/query-keys.ts";

export function useWebSocketSetup(webSocketSetupType: WebSocketSetupType) {
  const { t } = useTranslation();
  const token = lsLoad(StorageEnum.Jwt);
  const socketUrl = `${getWebSocketUrl()}/token=${token}`;
  const documentReady = useApplicationReady();
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setIsWebSocketConnected } = useWebSocketProvider();
  const { wsRef, keyRef, onStatusUpdate } = webSocketSetupType;
  const { logInfo, logError } = useLogging();
  const queryClient = useQueryClient();

  useEffect(() => {
    const hasToken = Boolean(token);
    logInfo(`document read: ${documentReady}`, "useWebSocketSetup");
    log("document read:", documentReady);
    // Unambiguous app log (not Vite). In Network tab, look for ws://localhost:3000, not 5173.
    console.log("[App WebSocket] effect ran", {
      documentReady,
      hasToken,
      wsHost: getWebSocketUrl(),
    });

    if (!hasToken) {
      logInfo("web socket skipped: no token", "useWebSocketSetup");
      return;
    }

    logInfo(`web socket url: ${socketUrl}`, "useWebSocketSetup");
    log("web socket url:", socketUrl);
    log("[App WebSocket] connecting to:", socketUrl);

    let ws: WebSocket;

    const setup = async (showSuccessMessage: boolean = false) => {
      if (!token) {
        return;
      }

      try {
        keyRef.current = await importAesKey(getBase64SharedKey());
      } catch (err) {
        logError(`importAesKey failed: ${err}`, "useWebSocketSetup", v4());
        error("importAesKey failed:", err);
        return;
      }

      try {
        ws = new WebSocket(socketUrl);
      } catch (err) {
        logError(
          `WebSocket constructor failed: ${err}`,
          "useWebSocketSetup",
          v4(),
        );
        error("WebSocket constructor failed:", err);
        return;
      }
      wsRef.current = ws;

      const handleOpen = () => {
        setIsWebSocketConnected(true);

        if (showSuccessMessage) {
          const market: MarketMarketIO = lsLoad(StorageEnum.Market);

          if (market?.id) {
            const webSocketType: WebSocketType = {
              type: MessageEnum.Broadcast,
              marketId: market.id,
              data: null,
            };

            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify(webSocketType));
            } else {
              logError(
                "web socket not ready. cannot send message yet",
                "useWebSocketSetup",
                v4(),
              );
              error("web socket not ready. cannot send message yet");
            }
          }

          toast.success(t("success.webSocketConnected"), toastOptions);
        }

        logInfo("connected to web socket", "useWebSocketSetup");
        log("connected to web socket");
      };

      ws.onopen = () => handleOpen();

      ws.onerror = (err) => {
        setIsWebSocketConnected(false);
        logError(`web socket error: ${err}`, "useWebSocketSetup", v4());
        error("web socket error:", err);
        ws.close();
      };
      ws.onclose = (e) => {
        setIsWebSocketConnected(false);
        reconnectWithBackoff();
        logInfo(
          `disconnected from web socket server: ${e.reason}`,
          "useWebSocketSetup",
        );
        log("disconnected from web socket server:", e.reason);
      };

      ws.onmessage = async (event) => {
        try {
          const rawData = event.data;

          if (rawData) {
            const webSocketType: WebSocketType = JSON.parse(rawData);

            switch (webSocketType.type) {
              case MessageEnum.Logout:
                toast.warning(t("ui.adminLogout"), {
                  ...toastOptions,
                  onClose: () => {
                    lsClear();
                    reloadPage();
                  },
                });
                break;
              case MessageEnum.Broadcast:
              case MessageEnum.Update: {
                // From here on, the WebSocket event is used as a pure signal.
                // The actual data comes from the API (React Query),
                // which already applies the user's permissions/limitations.
                const market: MarketMarketIO = lsLoad(StorageEnum.Market);
                const currentMarketId = market?.id;

                if (
                  currentMarketId != null &&
                  webSocketType.marketId === currentMarketId
                ) {
                  // Relevant market -> invalidate query so the API reloads filtered data.
                  const marketIdAsString = currentMarketId.toString();
                  queryClient.invalidateQueries({
                    queryKey:
                      queryKeys.marketComponents.byMarket(marketIdAsString),
                  });
                }
                break;
              }
              default:
                break;
            }
          }
        } catch (err) {
          logError(
            `invalid web socket message: ${event.data}; ${err}`,
            "useWebSocketSetup",
            v4(),
          );
          error("invalid web socket message:", event.data, err);
        }
      };

      // Firefox (and others) can fire "open" before onopen is set when the connection
      // is very fast (e.g. localhost). If the socket is already OPEN, run handleOpen now.
      if (ws.readyState === WebSocket.OPEN) {
        handleOpen();
      }
    };

    const setupSafe = (showSuccessMessage: boolean = false) => {
      setup(showSuccessMessage).catch((err) => {
        logError(`setup() failed: ${err}`, "useWebSocketSetup", v4());
        error("WebSocket setup() failed:", err);
      });
    };

    const reconnectWithBackoff = () => {
      if (reconnectTimeout.current) return;

      reconnectTimeout.current = setTimeout(() => {
        reconnectTimeout.current = null;
        setupSafe(true);
      }, 10000);
    };

    setupSafe();

    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        (!wsRef.current || wsRef.current.readyState > 1)
      ) {
        logInfo("page visible, attempting reconnect...", "useWebSocketSetup");
        log("page visible, attempting reconnect...");
        setupSafe(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      logInfo("closing web socket", "useWebSocketSetup");
      log("closing web socket");

      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }

      document.removeEventListener("visibilitychange", handleVisibility);

      if (ws) {
        ws.close();
      }
    };
  }, [
    documentReady,
    socketUrl,
    setIsWebSocketConnected,
    t,
    wsRef,
    keyRef,
    onStatusUpdate,
    logInfo,
    logError,
    token,
    queryClient,
  ]);
}
